
import { useState, useEffect } from "react";
import { Bell, User, Menu, LogOut, AlertTriangle, Package, Building2, ShoppingBag } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { BackButton } from "@/components/ui/back-button";
import { useDeviceInfo } from "@/hooks/use-device-info";
import { useUserData } from "@/hooks/use-user-data";
import { getResponsiveSpacing, getResponsiveTextSize } from "@/lib/utils";
import { inventoryAPI } from "@/services/api";
import { useQuery } from "@tanstack/react-query";
import { useOrderNotifications } from "@/hooks/use-order-notifications";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export interface ResponsiveHeaderProps {
  onMenuToggle?: () => void;
  showMenuButton?: boolean;
  title?: string;
}

export function Header({ 
  onMenuToggle, 
  showMenuButton = false, 
  title 
}: ResponsiveHeaderProps) {
  const { isMobile, isTablet, screenSize } = useDeviceInfo();
  const navigate = useNavigate();
  const { setIsAuthenticated } = useAuth();
  const { toast } = useToast();
  
  const { userName, userEmail, userInitials, branchName } = useUserData();

  // Website orders waiting to be fulfilled — badge count + arrival chime.
  // Declared exactly once: the previous attempt declared this hook twice in
  // the same scope, which is a TS redeclaration error and broke the build.
  const { open: openOrders } = useOrderNotifications();

  const displayTitle = title || '';

  // Fetch all products and calculate low stock items
  const { data: allProducts = [] } = useQuery({
    queryKey: ['products'],
    queryFn: inventoryAPI.getProducts,
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });

  // Calculate low stock products and notifications count
  const lowStockProducts = allProducts.filter(product => 
    product.quantity <= (product.reorder_level || 10) || product.quantity === 0
  );
  
  const notifications = lowStockProducts.length;

  const handleLogout = () => {
    console.log('Header logout clicked');
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setIsAuthenticated(false);
    toast({
      title: "Logged Out",
      description: "You have been successfully logged out.",
    });
    navigate("/login");
  };

  const handleNotifications = () => {
    console.log('🔔 Notification button clicked, notifications count:', notifications);
    
    if (notifications === 0) {
      toast({
        title: "No Alerts",
        description: "All products are well stocked!",
      });
      return;
    }

    console.log('🔔 Navigating to inventory with low stock filter');
    
    // Navigate to inventory with low stock filter
    navigate("/inventory", { 
      state: { 
        filterLowStock: true,
        showLowStockOnly: true 
      } 
    });
    
    toast({
      title: `${notifications} Stock Alert${notifications > 1 ? 's' : ''}`,
      description: `Showing ${notifications} product${notifications > 1 ? 's' : ''} that need attention`,
    });
  };

  return (
    <header 
      className={`
        /* Apple-style material: the bar is translucent so content scrolls
           underneath it, and the hard 1px rule below is replaced by a soft
           scroll-edge fade (no floating chrome with a sliced-off edge). */
        bg-card/85 backdrop-blur-md border-b border-border/50 shadow-soft
        after:pointer-events-none after:absolute after:inset-x-0 after:top-full after:h-3
        after:bg-gradient-to-b after:from-border/60 after:to-transparent
        h-14 sm:h-16 
        flex items-center 
        ${getResponsiveSpacing({ 
          mobile: '4', 
          tablet: '6', 
          desktop: '6',
          property: 'px' 
        })}
        sticky top-0 z-30
        ${screenSize === 'mobile' ? 'pt-safe-area-inset-top' : ''}
      `}
      role="banner"
    >
      {/* Left Section - Menu Button, Back Button and Title */}
      <div className="flex items-center flex-1 min-w-0">
        {showMenuButton && onMenuToggle && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onMenuToggle();
            }}
            className={`
              mr-2 h-12 w-12 
              min-h-[48px] min-w-[48px] 
              touch-manipulation 
              lg:hidden
              relative z-50
              active:bg-accent
              hover:bg-accent
              cursor-pointer
              select-none
              flex items-center justify-center
              rounded-md
              transition-colors
              focus-visible:outline-none 
              focus-visible:ring-2 
              focus-visible:ring-ring 
              focus-visible:ring-offset-2
              border-0
              bg-transparent
              text-foreground
            `}
            aria-label="Open navigation menu"
            aria-expanded="false"
            style={{ 
              WebkitTapHighlightColor: 'rgba(0,0,0,0.1)',
              touchAction: 'manipulation',
              WebkitUserSelect: 'none',
              userSelect: 'none',
              pointerEvents: 'auto',
              outline: 'none'
            }}
          >
            <Menu className="h-6 w-6" />
          </button>
        )}
        
        {/* Back Button - Shows on all pages except home */}
        <BackButton 
          size="icon"
          className={`
            mr-2 
            ${isMobile ? 'h-9 w-9' : 'h-10 w-10'}
          `}
          showText={false}
        />
        
        <h1 
          className={`
            font-semibold truncate
            ${getResponsiveTextSize({ 
              mobile: 'lg', 
              tablet: 'xl', 
              desktop: 'xl' 
            })}
            ${isMobile ? 'max-w-[150px]' : 'max-w-[200px]'}
          `}
        >
          {displayTitle}
        </h1>
      </div>
      
      {/* Right Section - Actions */}
      <div className={`
        flex items-center 
        ${isMobile ? 'space-x-2' : 'space-x-4'}
      `      }>
        {/* Website Orders Button — grows as buyer orders come in */}
        <div className="relative">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/orders")}
            className={`
              h-10 w-10
              min-h-[44px] min-w-[44px]
              touch-manipulation
              ${isMobile ? 'h-9 w-9' : 'h-10 w-10'}
            `}
            aria-label={
              openOrders > 0
                ? `Website orders: ${openOrders} waiting to be fulfilled`
                : "No website orders waiting"
            }
          >
            <ShoppingBag className="h-5 w-5" />
            {openOrders > 0 && (
              <span
                className="absolute -top-1 -right-1 bg-green-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center border-2 border-white"
                aria-label={`${openOrders} new website orders`}
              >
                {openOrders > 99 ? "99+" : openOrders}
              </span>
            )}
          </Button>
        </div>

        {/* Notifications Button */}
        <div className="relative">
          <Button 
            variant={notifications > 0 ? "default" : "ghost"}
            size="icon"
            onClick={handleNotifications}
            className={`
              h-10 w-10 
              min-h-[44px] min-w-[44px] 
              touch-manipulation
              ${isMobile ? 'h-9 w-9' : 'h-10 w-10'}
              ${notifications > 0 ? 'bg-orange-500 hover:bg-orange-600 text-white' : ''}
            `}
            aria-label={
              notifications > 0 
                ? `Stock alerts: ${notifications} item${notifications > 1 ? 's' : ''} need attention`
                : 'No stock alerts'
            }
          >
            {notifications > 0 ? (
              <AlertTriangle className="h-5 w-5" />
            ) : (
              <Bell className="h-5 w-5" />
            )}
            {notifications > 0 && (
              <span 
                className="absolute -top-1 -right-1 bg-red-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center border-2 border-white"
                aria-label={`${notifications} stock alerts`}
              >
                {notifications > 99 ? '99+' : notifications}
              </span>
            )}
          </Button>
        </div>
        
        {/* User Profile Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              className={`
                relative rounded-full
                min-h-[44px] min-w-[44px] 
                touch-manipulation
                ${isMobile ? 'h-9 w-9' : 'h-10 w-10'}
              `}
              aria-label="User menu"
            >
              <Avatar className={isMobile ? "h-8 w-8" : "h-9 w-9"}>
                <AvatarFallback className="bg-primary text-white text-sm">
                  {userInitials}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent 
            className={`
              ${isMobile ? 'w-48' : 'w-56'} 
              ${isMobile ? 'mr-2' : ''}
            `} 
            align="end" 
            forceMount
            sideOffset={8}
          >
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className={`font-medium leading-none ${isMobile ? 'text-sm' : 'text-sm'}`}>
                  {userName}
                </p>
                <p className={`leading-none text-muted-foreground ${isMobile ? 'text-xs' : 'text-xs'}`}>
                  {userEmail}
                </p>
              </div>
            </DropdownMenuLabel>
            {branchName && (
              <div className="px-2 pb-2">
                <span
                  className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-1 text-xs text-muted-foreground"
                  title="You are viewing data for this branch only"
                >
                  <Building2 className="h-3 w-3" />
                  {branchName}
                </span>
              </div>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              className={`${isMobile ? 'py-3' : 'py-2'} cursor-pointer`}
              onClick={() => {
                toast({
                  title: "Profile",
                  description: "Profile settings coming soon!",
                });
              }}
            >
              <User className="mr-2 h-4 w-4" />
              <span>Profile</span>
            </DropdownMenuItem>
            <DropdownMenuItem 
              className={`${isMobile ? 'py-3' : 'py-2'} cursor-pointer`}
              onClick={handleNotifications}
            >
              <Bell className="mr-2 h-4 w-4" />
              <div className="flex flex-col">
                <span>Stock Alerts</span>
                {notifications > 0 && (
                  <span className="text-xs text-muted-foreground">
                    {notifications} item{notifications > 1 ? 's' : ''} need attention
                  </span>
                )}
              </div>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              className={`${isMobile ? 'py-3' : 'py-2'} cursor-pointer text-red-600 hover:text-red-700 hover:bg-red-50`}
              onClick={handleLogout}
            >
              <LogOut className="mr-2 h-4 w-4" />
              <span>Log out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
